import {getUserTraits, updateUserTraits} from '@commercial-website/analytics'
import * as EmailValidator from 'email-validator'
import Cookies from 'js-cookie'
import React from 'react'

import {
    emailSignupActioned,
    EmailSignupActionedAction,
    performanceMarketingSignupActioned,
    PerformanceMarketingSignupActionedAction,
    Referral,
} from '@/analytics/segment'
import type {Completions} from '@/components/navattic-demo'
import {cookiesMap} from '@/config/cookies'
import {useFlagVariation, useTrackExperimentEvent} from '@/feature-flags/launch-darkly'
import {useHubspotProperties} from '@/hooks/use-hubspot-properties'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useNavigate from '@/hooks/use-navigate'
import {useSignupRedirect} from '@/hooks/use-signup-redirect'
import {useLocalStorage} from '@/hooks/use-storage'
import useSubmit from '@/hooks/use-submit'
import {useSubmitHubspotForm} from '@/hooks/use-submit-hubspot-form'
import {useGoogleReCaptcha} from '@/providers/google-recaptcha'
import {useLocale} from '@/providers/locale'
import {usePageInfo} from '@/providers/page-info'
import type {HubspotContactProps} from '@/types/hubspot-contact-props'
import {
    LatestLeadSourceCaptureType,
    LifecycleStages,
    HubspotPartnerChannel,
    LeadType,
} from '@/types/marketing-campaigns'
import {isEmptyObject} from '@/utils/is-empty-object'
import {isGenericEmail} from '@/utils/is-generic-email'
import {wait} from '@/utils/wait'

export interface Args {
    redirectPath?: string
    form: string
    trackQualifyingLeads?: boolean
    params?: Record<string, string> | Record<string, string[]>
    isPartner?: boolean
}

export enum ErrorType {
    INVALID_EMAIL = 'INVALID_EMAIL',
    NON_WORK_EMAIL = 'NON_WORK_EMAIL',
}

const useSignup = (
    {form, trackQualifyingLeads = true, isPartner, ...props}: Args,
    formId: string,
) => {
    const currentLocale = useLocale()
    const [email, setEmail] = React.useState('')
    const {pageContext} = usePageInfo()
    const isCompanyEmail = useIsCompanyEmail(email)
    const isValidEmail = EmailValidator.validate(email)
    const navigate = useNavigate()
    const [navatticDemoCompletion] = useLocalStorage<Completions>('navatticDemoCompletion', {})

    const navatticParams = !isEmptyObject(navatticDemoCompletion)
        ? {
              navattic_flows_completed: navatticDemoCompletion.completions,
              navattic_flow_progress: navatticDemoCompletion.flowProgress,
          }
        : null

    const selfSignupPartnerRedirectProps = {
        redirectPath: `${process.env.GATSBY_PLEO_APP_URL}/partner/signup`,
        params: {email, country: currentLocale.country},
    }

    const selfSignupPartnerHubSpotProperties: Partial<HubspotContactProps> = {
        lead_source_latest_capture_type: LatestLeadSourceCaptureType.SelfOnboardingPartner,
        type_of_lead: LeadType.PartnerSelfSignup,
        partner_channel: HubspotPartnerChannel.AccountingPartner,
        country: currentLocale.country,
    }

    const redirectPath = useSignupRedirect(
        isPartner
            ? selfSignupPartnerRedirectProps
            : {
                  redirectPath: props.redirectPath,
                  params: {
                      ...props.params,
                      email,
                      ...navatticParams,
                  },
              },
    )

    const getVariation = useFlagVariation('aa-commercial-website-test', false)
    // We're not using the result of this variation since it's an A/A test
    // however we still call it to send an exposure event - it is at this moment
    // we are acknowledging the user is entering the experiment
    getVariation()
    const trackExperimentEvent = useTrackExperimentEvent()

    const {recaptchaHubspotSubmit} = useSubmitHubspotForm(formId, false)
    const hubspotProperties = useHubspotProperties()
    const {loadReCaptcha} = useGoogleReCaptcha()
    const external_id = Math.random().toString(16).substring(2)

    const isReferralLandingPage = pageContext.uid === 'referrals' ? Referral.Yes : Referral.No

    const handleSignup = async (e: React.FormEvent<HTMLFormElement> | undefined) => {
        e?.preventDefault()

        if (!isValidEmail || !isCompanyEmail) {
            emailSignupActioned({
                action: EmailSignupActionedAction.Blocked,
                referral: isReferralLandingPage,
                form,
                email,
            })
        }

        if (!isValidEmail) {
            throw new Error(ErrorType.INVALID_EMAIL)
        }

        if (!isCompanyEmail) {
            throw new Error(ErrorType.NON_WORK_EMAIL)
        }

        trackExperimentEvent('email-signup-actioned')
        emailSignupActioned({
            action: EmailSignupActionedAction.Submitted,
            referral: isReferralLandingPage,
            form,
            email,
        })

        updateUserTraits({
            external_id,
            externalId: external_id,
            form,
            email,
        })

        if (trackQualifyingLeads && !email?.match(/.*gmail.*/i)) {
            const userTraits = getUserTraits()
            performanceMarketingSignupActioned(
                {
                    action: PerformanceMarketingSignupActionedAction.Started,
                    value: 1,
                    fbc: Cookies.get(cookiesMap.metaClickId) || '',
                    fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
                    ttclid: Cookies.get(cookiesMap.tikTokPixelId) || '',
                    li_fat_id: Cookies.get(cookiesMap.linkedinClickId) || '',
                },
                userTraits,
            )
        }

        await recaptchaHubspotSubmit({
            ...hubspotProperties,
            email,
            genericEmail: isGenericEmail(email),
            lead_source_latest_capture_type: LatestLeadSourceCaptureType.SelfOnboarding,
            lifecyclestage: LifecycleStages.Subscriber,
            ...(isPartner && selfSignupPartnerHubSpotProperties),
        })

        /**
         * A short timeout to give the track calls more time to complete
         * before redirecting. Works like Segment's `trackForm` method, but
         * allows us to programmatically invoke the track events.
         *
         * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track-form
         */
        await wait(400)

        if (redirectPath.startsWith('http')) {
            window.location.assign(redirectPath)
        } else {
            navigate(redirectPath)
        }
    }

    const {handleSubmit, loading, error, clearError} = useSubmit(handleSignup)

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        loadReCaptcha()
        clearError()
        setEmail(e.target.value)
    }

    return {
        handleSubmit,
        handleEmailChange,
        email,
        loading,
        error,
        clearError,
        setEmail,
    }
}

export default useSignup
